/** 會動的 SVG 圖示。
 *
 *  全部用 SMIL / CSS 寫在 SVG 內，不靠 JS 逐幀 —— 分頁切到背景時瀏覽器
 *  會自動暫停，不會空轉吃電。
 *  一律尊重 prefers-reduced-motion：使用者關掉動畫時只留靜態圖形。
 */
import { cn } from '@/lib/utils';

interface IconProps {
  className?: string;
  size?: number;
}

/** 鏡頭：光圈葉片緩慢旋轉，中心紅點呼吸。 */
export function LensIcon({ className, size = 34 }: IconProps) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none"
         className={cn('motion-reduce:[&_*]:animate-none', className)} aria-hidden="true">
      <circle cx="24" cy="24" r="19" stroke="currentColor" strokeWidth="1.8" opacity=".6" />
      <g stroke="currentColor" strokeWidth="1.7" opacity="1">
        <g>
          {[0, 60, 120, 180, 240, 300].map((deg) => (
            <path key={deg} d="M24 8 L33 21" transform={`rotate(${deg} 24 24)`} strokeLinecap="round" />
          ))}
          <animateTransform attributeName="transform" type="rotate"
                            from="0 24 24" to="360 24 24" dur="18s" repeatCount="indefinite" />
        </g>
      </g>
      <circle cx="24" cy="24" r="6.5" stroke="currentColor" strokeWidth="1.9" />
      <circle cx="24" cy="24" r="2.6" fill="#EF4444">
        <animate attributeName="opacity" values="1;.35;1" dur="2.4s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

/** 聲波：三段弧線由內而外依序擴散。 */
export function WaveIcon({ className, size = 34 }: IconProps) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none"
         className={cn('motion-reduce:[&_*]:animate-none', className)} aria-hidden="true">
      {/* 麥克風本體 */}
      <rect x="20" y="10" width="8" height="17" rx="4"
            stroke="currentColor" strokeWidth="1.9" />
      <path d="M15 24a9 9 0 0018 0" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      <path d="M24 33v5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      {/* 擴散的聲波 */}
      {[0, 0.6, 1.2].map((delay, i) => (
        <path key={i} d={`M${34 + i * 3} ${18 - i * 2}a${7 + i * 3} ${7 + i * 3} 0 010 ${12 + i * 4}`}
              stroke="#EF4444" strokeWidth="1.9" strokeLinecap="round" opacity="0">
          <animate attributeName="opacity" values="0;.9;0" dur="2.4s"
                   begin={`${delay}s`} repeatCount="indefinite" />
        </path>
      ))}
    </svg>
  );
}

/** 歸檔：層疊的磁碟，掃描線由上而下週期掃過。 */
export function ArchiveIcon({ className, size = 34 }: IconProps) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none"
         className={cn('motion-reduce:[&_*]:animate-none', className)} aria-hidden="true">
      <ellipse cx="24" cy="14" rx="13" ry="5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M11 14v9c0 2.8 5.8 5 13 5s13-2.2 13-5v-9"
            stroke="currentColor" strokeWidth="1.6" />
      <path d="M11 23v9c0 2.8 5.8 5 13 5s13-2.2 13-5v-9"
            stroke="currentColor" strokeWidth="1.6" opacity=".55" />
      <circle cx="31.5" cy="31.5" r="1.8" fill="#10B981">
        <animate attributeName="opacity" values=".25;1;.25" dur="1.8s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

/** 連線脈衝：節點之間的資料流，小點沿線移動。 */
export function LinkPulseIcon({ className, size = 34 }: IconProps) {
  return (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none"
         className={cn('motion-reduce:[&_*]:animate-none', className)} aria-hidden="true">
      <path id="pulse-path" d="M9 34 L20 20 L31 27 L39 13"
            stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"
            strokeLinejoin="round" opacity=".5" />
      {[9, 20, 31, 39].map((x, i) => (
        <circle key={x} cx={x} cy={[34, 20, 27, 13][i]} r="2.4"
                fill="currentColor" opacity=".8" />
      ))}
      <circle r="2.8" fill="#EF4444">
        <animateMotion dur="3s" repeatCount="indefinite"
                       path="M9 34 L20 20 L31 27 L39 13" />
        <animate attributeName="opacity" values="0;1;1;0" dur="3s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

/** 盾牌：安全連線，邊框描邊一圈一圈掃過。 */
export function ShieldPulseIcon({ className, size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none"
         className={cn('motion-reduce:[&_*]:animate-none', className)} aria-hidden="true">
      <path d="M12 2.5 4.5 5.8v5.4c0 4.6 3.2 8.6 7.5 10 4.3-1.4 7.5-5.4 7.5-10V5.8z"
            stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"
            strokeDasharray="60" strokeDashoffset="0">
        <animate attributeName="stroke-dashoffset" values="60;0" dur="2.2s"
                 repeatCount="indefinite" />
      </path>
      <path d="M9 12l2.2 2.2L15.5 10" stroke="#10B981" strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
